import { defaultError } from '@qpr/client-library';
import api, { ApiRequest, ApiResult } from '../../../api/api-caller';
import { ApiEndpoint } from '../../../api/api-consts';
import { fetchRangeColors, getRangeColors, RangeColor } from '../../../utils/rangecolor-utils';
import initialState, {
  BusinessModelCanvasState,
  CanvasCellState as CanvasCell,
  CellRowState as CellRow
} from '../business-model-canvas-state';

const fetchGroups = async (corporateId: string): Promise<ApiResult> => {
  const groupsQuery: ApiRequest = {
    body: {
      parentId: corporateId,
      objecttype: 'Group'
    },
    method: 'POST'
  };
  const groupsResult: ApiResult = await api.asyncCallApi(ApiEndpoint.childelements, groupsQuery);

  return groupsResult;
};

const fetchGroupItems = async (parentIds: String[]): Promise<ApiResult> => {
  const groupItemQuery: ApiRequest = {
    body: {
      parentId: parentIds.join(),
      groupelements: true,
      attributes:
        'name,relationends.belongstogroup(as=groupId), functionasattribute(functionasattribute="scorecardlinks", as="measureId")'
    },
    method: 'POST'
  };
  const groupItemsResult: ApiResult = await api.asyncCallApi(
    ApiEndpoint.childelements,
    groupItemQuery
  );

  return groupItemsResult;
};

const getGroups = (groupsResult: ApiResult) => {
  const groups = groupsResult.data.map((group: CanvasCell) => {
    return { ...group, rows: [] };
  });

  return groups;
};

const getGroupItems = (groupItemResult: ApiResult) => {
  return groupItemResult.data;
};

const addGroupItemsToGroups = (groupItems: CellRow[], groups: CanvasCell[]) => {
  groups.forEach((group: CanvasCell) => {
    groupItems.forEach((groupItem: CellRow) => {
      if (groupItem.groupId && groupItem.groupId.includes(group.id as string)) {
        group.rows.push(groupItem);
      }
    });
  });

  return groups;
};

const errorHandler = (apiResult: ApiResult): BusinessModelCanvasState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

const addRangeColorsToGroupItems = (rangeColors: RangeColor[], groupItems: CellRow[]) => {
  rangeColors.forEach(rangeColor => {
    const foundList = groupItems.filter(
      (groupItem: CellRow) => groupItem.measureId === rangeColor.id
    );

    if (foundList.length > 0) {
      for (let i = 0; i < foundList.length; i += 1) {
        foundList[i].rangecolor =
          rangeColor.rangecolor === '000000' ? undefined : `#${rangeColor.rangecolor}`;
      }
    }
  });
};

const doGetData = async (corporateId: string): Promise<BusinessModelCanvasState> => {
  try {
    const groupsResult = await fetchGroups(corporateId);
    if (!groupsResult.ok) {
      return errorHandler(groupsResult);
    }

    const groups = getGroups(groupsResult);

    if (groups.length === 0)
      return {
        ...initialState,
        error: {
          message: 'No groups to show.',
          details: `No Business Model Canvas groups found for Business process id ${corporateId}`
        }
      };

    const groupIds = groups.map((group: CanvasCell) => group.id);

    const groupItemsResult = await fetchGroupItems(groupIds);

    if (!groupItemsResult.ok) {
      return errorHandler(groupItemsResult);
    }

    const groupItems = getGroupItems(groupItemsResult);
    const measureIds = groupItems
      .map((groupItem: CellRow) => groupItem.measureId)
      .filter((measureId: string) => measureId !== undefined && measureId !== '');
    if (measureIds.length > 0) {
      const rangeColorsResult = await fetchRangeColors(measureIds);

      if (!rangeColorsResult.ok) {
        return errorHandler(rangeColorsResult);
      }

      const rangeColors = getRangeColors(rangeColorsResult);

      addRangeColorsToGroupItems(rangeColors, groupItems);
    }
    addGroupItemsToGroups(groupItems, groups);

    return { ...initialState, elements: groups };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface BMCViewModelType {
  fetchData(): Promise<BusinessModelCanvasState>;
  getData(): Promise<BusinessModelCanvasState>;
  getId(): string;
}

const createBusinessModelCanvasViewModel = (corporateID: string): BMCViewModelType => {
  let data: BusinessModelCanvasState;
  const getId = (): string => corporateID;
  const fetchData = async (): Promise<BusinessModelCanvasState> => {
    if (viewModelCache[corporateID]) {
      console.log('From cache', viewModelCache[corporateID]);
      data = viewModelCache[corporateID];
    } else {
      data = await doGetData(corporateID);
      viewModelCache[corporateID] = data;
    }
    return data;
  };

  const getData = async (): Promise<BusinessModelCanvasState> => {
    if (data) {
      console.log('getData already populated');
      return data;
    }
    await fetchData();
    return data;
  };

  return {
    getId,
    fetchData,
    getData
  };
};

export default createBusinessModelCanvasViewModel;
